import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  CalendarDays,
  CheckCircle2,
  Download,
  Mail,
  MapPin,
  Store,
} from 'lucide-react';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { UserAvatar } from '../components/shared/UserAvatar';
import { PerformanceSendSheet } from '../components/performance/PerformanceSendSheet';
import { downloadPdf } from '../utils/pdfDownload';
import { formatDateShort } from '../utils/datetime';
import type { Visit } from '../types';
import type {
  AgentVisitStats,
  PerformancePeriod,
  PublicAgentProfile,
} from '../types/performance';
import { PERFORMANCE_PERIODS } from '../types/performance';

const emptyStats: AgentVisitStats = {
  visits_today: 0,
  visits_last_7_days: 0,
  visits_this_month: 0,
  visits_total: 0,
  placements_secured_this_month: 0,
  placements_secured_total: 0,
};

interface RankBits {
  rank: number;
  score: number;
  visits: number;
  placements: number;
}

export const AgentPerformance: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const { user, isTeamAdmin, isSuperAdmin } = useAuth();
  const canExport = isTeamAdmin || isSuperAdmin;
  const [period, setPeriod] = useState<PerformancePeriod>('month');
  const [profile, setProfile] = useState<PublicAgentProfile | null>(null);
  const [stats, setStats] = useState<AgentVisitStats>(emptyStats);
  const [rank, setRank] = useState<RankBits | null>(null);
  const [recent, setRecent] = useState<Visit[]>([]);
  const [loading, setLoading] = useState(true);
  const [sendOpen, setSendOpen] = useState(false);

  useEffect(() => {
    if (!userId) return;
    const load = async () => {
      setLoading(true);
      try {
        const [profileRes, statsRes, boardRes, visitsRes] = await Promise.all([
          api.get(`/users/${userId}`),
          api.get(`/visits/stats?user_id=${userId}`),
          api.get(`/visits/leaderboard?period=${period}`),
          api.get(`/visits?user_id=${userId}&limit=8`),
        ]);
        setProfile(profileRes.data);
        setStats({ ...emptyStats, ...(statsRes.data || {}) });
        const entry = (boardRes.data?.entries || []).find(
          (e: RankBits & { user_id: string }) => e.user_id === userId
        );
        setRank(entry || null);
        setRecent(visitsRes.data?.data || []);
      } catch {
        toast.error('Could not load this profile');
        setProfile(null);
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, [userId, period]);

  const onDownload = async () => {
    if (!userId) return;
    try {
      await downloadPdf(
        `/performance/pdf?scope=user&user_id=${userId}&period=${period}`,
        `performance-${period}.pdf`
      );
    } catch {
      toast.error('Could not download overview');
    }
  };

  if (loading) {
    return <p className="text-sm text-gray-500 py-12 text-center">Loading…</p>;
  }
  if (!profile) {
    return (
      <div className="max-w-lg mx-auto py-12 text-center space-y-3">
        <p className="text-sm text-gray-500">This teammate is not on your team.</p>
        <Link to="/leaderboard" className="text-sm font-bold text-gray-900">
          Back to leaderboard
        </Link>
      </div>
    );
  }

  const isMe = profile.id === user?.id;
  const prefill = profile.signatureEmail || profile.email || '';

  return (
    <div className="max-w-lg mx-auto space-y-4 pb-8">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400">
            Agent
          </p>
          <h1 className="text-base font-black text-gray-900">{profile.fullName}</h1>
          <p className="text-xs text-gray-500">
            {[profile.jobTitle, profile.city].filter(Boolean).join(' · ') || 'Team member'}
          </p>
        </div>
        <UserAvatar
          userId={profile.id}
          fullName={profile.fullName}
          photoUrl={profile.photoUrl}
          size="md"
        />
      </div>

      {profile.bio ? (
        <p className="text-sm text-gray-600">{profile.bio}</p>
      ) : null}

      <div className="flex rounded-xl border border-gray-200 overflow-hidden bg-white">
        {PERFORMANCE_PERIODS.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => setPeriod(p.id)}
            className={`flex-1 min-h-[40px] px-2 text-xs font-bold ${
              period === p.id ? 'bg-gray-900 text-white' : 'text-gray-600'
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 px-4 py-3">
        <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400">
          This period
        </p>
        <p className="text-sm font-black text-gray-900 mt-1">
          {rank
            ? `Rank #${rank.rank} · ${rank.score} pts · ${rank.visits} visits · ${rank.placements} placements`
            : 'No score yet'}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <Stat title="Today" value={stats.visits_today} icon={CalendarDays} />
        <Stat title="7 days" value={stats.visits_last_7_days} icon={MapPin} />
        <Stat title="Month" value={stats.visits_this_month} icon={Store} />
        <Stat title="Total" value={stats.visits_total} icon={Store} />
        <Stat
          title="Placements month"
          value={stats.placements_secured_this_month}
          icon={CheckCircle2}
        />
        <Stat
          title="Placements total"
          value={stats.placements_secured_total}
          icon={CheckCircle2}
        />
      </div>

      {canExport ? (
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => void onDownload()}
            className="flex-1 min-h-[44px] rounded-xl border border-gray-200 text-sm font-bold
              text-gray-800 flex items-center justify-center gap-2"
          >
            <Download size={16} /> Download
          </button>
          <button
            type="button"
            onClick={() => setSendOpen(true)}
            className="flex-1 min-h-[44px] rounded-xl border border-gray-200 text-sm font-bold
              text-gray-800 flex items-center justify-center gap-2"
          >
            <Mail size={16} /> Send
          </button>
        </div>
      ) : null}

      {isMe ? (
        <Link to="/profile" className="block text-center text-xs font-bold text-gray-500">
          Edit your profile
        </Link>
      ) : null}

      <div>
        <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2">
          Recent visits
        </p>
        <ul className="bg-white rounded-2xl border border-gray-100 divide-y divide-gray-50">
          {recent.length === 0 ? (
            <li className="px-4 py-6 text-sm text-gray-500 text-center">No visits yet</li>
          ) : (
            recent.map((visit) => (
              <li key={visit.id} className="px-4 py-2.5">
                <p className="text-sm font-bold text-gray-900 truncate">
                  {visit.pos_name || 'PoS'}
                </p>
                <p className="text-xs text-gray-500">
                  {formatDateShort(visit.visit_date)}
                  {visit.visit_type === 'first_time' ? ' · First visit' : ' · Revisit'}
                </p>
              </li>
            ))
          )}
        </ul>
      </div>

      <Link to="/leaderboard" className="block text-center text-xs font-bold text-gray-400">
        Back to leaderboard
      </Link>

      {canExport && userId ? (
        <PerformanceSendSheet
          open={sendOpen}
          onOpenChange={setSendOpen}
          scope="user"
          userId={userId}
          period={period}
          defaultTo={prefill}
        />
      ) : null}
    </div>
  );
};

function Stat({
  title,
  value,
  icon: Icon,
}: {
  title: string;
  value: number;
  icon: React.ElementType;
}) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 px-3 py-2.5">
      <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 flex items-center gap-1">
        <Icon size={12} /> {title}
      </p>
      <p className="text-lg font-black text-gray-900 tabular-nums mt-1">{value}</p>
    </div>
  );
}

export default AgentPerformance;
